import { GET_SUBSCRIPTIONS_URL, migrateSubscriptions } from './serialization/migrateSubscriptions'
import { Subscription } from '../../types/subscription'
import { SubscriptionServiceGetSubscriptionsJSON } from './types/jsons/SubscriptionServiceGetSubscriptionsJSON'
import { fetchSubscriptionServiceUrl } from './fetchSubscriptionServiceUrl'
import { getPlans } from './getPlans'
import compact from 'lodash/compact'
import uniq from 'lodash/uniq'

export interface GetSubscriptionsOptionsFilter {
  isCurrent?: boolean
}

export interface GetSubscriptionsOptions {
  filter?: GetSubscriptionsOptionsFilter
  token?: string
}

/**
 * Calls the subscription service GET /v2/plans endpoint.
 *
 * May be used as a fetcher for useSWR or by itself for SSR.
 *
 * @docs https://backstage.use1.dev.services.flowcode.systems/catalog/default/api/subscription-service-rest-api/definition#/Plans/get_v2_plans
 */
export async function getSubscriptions(options?: GetSubscriptionsOptions): Promise<Subscription[]> {
  const response = await fetchSubscriptionServiceUrl<SubscriptionServiceGetSubscriptionsJSON>({
    token: options?.token,
    url: GET_SUBSCRIPTIONS_URL,
    query: {
      'filter[isCurrent]': options?.filter?.isCurrent
    }
  })
  if ([404, 401, 403].includes(response.status)) {
    return []
  }
  if (!response.ok) {
    throw new Error(
      `Received http status ${response.status} from ${GET_SUBSCRIPTIONS_URL} endpoint`
    )
  }
  if (!response.json) {
    throw new Error(`The ${GET_SUBSCRIPTIONS_URL} endpoint did not return a json payload`)
  }

  const jsonSubscriptions = response.json.subscriptions || []

  // Load all associated plans
  const planIds = uniq(
    compact([
      ...jsonSubscriptions.map(jsonSubscription => jsonSubscription.planId),
      ...jsonSubscriptions.map(jsonSubscription => jsonSubscription.queuedDowngradePlanId)
    ])
  )
  const plans = planIds.length > 0 ? await getPlans({ filter: { planId: planIds } }) : []

  const subscriptions = migrateSubscriptions(jsonSubscriptions, plans)
  return subscriptions
}
